import { useFormContext } from "react-hook-form";
import { Input } from "@flint/ui";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@flint/ui/forms";

export interface SkuFieldProps {
  name: string;
  label: string;
  helper?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * SKU input that upper-cases as you type.
 *
 * A wholesale SKU is a code, not prose: `SKU-CT-RN-ECR`. Left free-form, the
 * same product gets entered as `sku-ct-rn-ecr` by one operator and
 * `SKU-CT-RN-ECR` by the next, and because SKU is immutable once an order
 * references it, the casing is permanent — it lands on invoices and line sheets
 * exactly as typed. Normalising at the keystroke is the only point where it can
 * still be fixed without a data migration.
 *
 * Its own component rather than a `transform` prop on `@flint/ui`'s `TextField`
 * because that primitive spreads the RHF field last (`{...inputProps} {...field}`),
 * so a caller cannot override `onChange`. Widening the shared primitive for one
 * caller is the bigger change.
 */
export function SkuField({ name, label, helper, placeholder, disabled }: SkuFieldProps) {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              {...field}
              value={field.value ?? ""}
              placeholder={placeholder}
              disabled={disabled}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              // Upper-cased on the way into form state, not via CSS
              // `text-transform`: that only changes what the glyph looks like,
              // and the lower-case string is what would be saved.
              onChange={(e) => field.onChange(e.target.value.toUpperCase())}
            />
          </FormControl>
          {helper ? <FormDescription>{helper}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
